import { Outlet, useLocation, useNavigate } from 'react-router-dom'

import { formatJobOutcomeType } from '@/features/jobs/lib/format-job-outcome-type'
import { COMPLETED_JOB_OUTCOMES } from '@/features/jobs/lib/job-outcome-options'
import type { JobType } from '@/features/jobs/types/job'
import {
  apiGetCompletedJobsByPartnerReport,
  apiGetCompletedJobsReport,
} from '@/features/reports/api/api-get-jobs-report'
import { DrilldownLink } from '@/features/reports/components/drilldown-link'
import { JobsReportFiltersPanel } from '@/features/reports/components/jobs-report-filters-panel'
import { JobsReportShell } from '@/features/reports/components/jobs-report-shell'
import { ReportCategoryChart } from '@/features/reports/components/report-category-chart'
import { useJobsReportPage } from '@/features/reports/hooks/use-jobs-report-page'
import { buildJobsReportBaseColumns } from '@/features/reports/lib/build-jobs-report-base-columns'
import { formatJobsReportFilterTitle } from '@/features/reports/lib/format-jobs-report-filter-title'
import { nextJobsReportSort } from '@/features/reports/lib/jobs-report-sort'
import {
  buildCompletedJobsReportSearchParams,
  completedJobsByOutcomePath,
  completedJobsByPartnerPath,
  defaultCompletedJobsReportSearchParams,
  parseCompletedJobsReportSearchParams,
} from '@/features/reports/lib/jobs-report-search-params'
import {
  emptyCompletedJobsReportFilters,
  type CompletedJob,
  type CompletedJobOutcome,
  type CompletedJobsReportFilters,
  type CompletedJobsReportSummary,
  type JobsByPartnerReportSummary,
} from '@/features/reports/types/jobs-report'
import {
  ListTable,
  LoadingSpinner,
  PaginationLinks,
  SelectField,
  type ListTableColumn,
  type SelectOption,
} from '@/shared/components'

type CompletedReportSummary =
  | CompletedJobsReportSummary
  | JobsByPartnerReportSummary

const emptySummary = (): CompletedReportSummary => ({
  by_outcome: [],
})

const outcomeOptions: SelectOption[] = COMPLETED_JOB_OUTCOMES.map(
  ({ value, label }) => ({
    value,
    label,
  })
)

const isPartnerSummary = (
  summary: CompletedReportSummary
): summary is JobsByPartnerReportSummary => 'by_partner' in summary

const extraPartnerRows = (reportSummary: CompletedReportSummary) =>
  isPartnerSummary(reportSummary) ? reportSummary.by_partner : []

export const CompletedJobsReportPage = () => {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const isPartnerView = pathname === '/reports/completed-jobs-by-partner'

  const {
    filters,
    page,
    chartType,
    sort,
    jobs,
    summary,
    pagination,
    isInitialLoading,
    error,
    isFiltersOpen,
    setIsFiltersOpen,
    updateSearch,
    chartTitle,
  } = useJobsReportPage<
    CompletedJobsReportFilters,
    CompletedJob,
    CompletedReportSummary
  >({
    parseSearchParams: parseCompletedJobsReportSearchParams,
    buildSearchParams: buildCompletedJobsReportSearchParams,
    defaultSearchParams: defaultCompletedJobsReportSearchParams,
    fetchReport: isPartnerView
      ? apiGetCompletedJobsByPartnerReport
      : apiGetCompletedJobsReport,
    emptySummary,
    formatFilterTitle: (nextFilters, labels) =>
      formatJobsReportFilterTitle(
        nextFilters,
        labels,
        nextFilters.outcome
          ? `Outcome: ${formatJobOutcomeType(nextFilters.outcome)}`
          : undefined
      ),
    loadErrorMessage: isPartnerView
      ? 'Failed to load completed jobs by partner report'
      : 'Failed to load completed jobs report',
    extraPartnerRows,
  })

  const outcomeTo = (outcome: CompletedJobOutcome) =>
    completedJobsByPartnerPath({ ...filters, outcome }, 1, chartType, sort)

  const partnerTo = (assignedPartnerId: number) =>
    completedJobsByOutcomePath(
      { ...filters, assigned_partner_id: assignedPartnerId },
      1,
      chartType,
      sort
    )

  const jobTypeTo = (jobType: JobType) => {
    const path = isPartnerView
      ? completedJobsByPartnerPath
      : completedJobsByOutcomePath

    return path({ ...filters, job_type: jobType }, 1, chartType, sort)
  }

  const columns: ListTableColumn<CompletedJob>[] = buildJobsReportBaseColumns({
    filters,
    partnerTo,
    jobTypeTo,
    primaryColumns: [
      {
        id: 'outcome',
        header: 'Outcome',
        cell: (job) => {
          const outcome = job.outcome
          if (!outcome) {
            return ''
          }

          return (
            <DrilldownLink
              label={formatJobOutcomeType(outcome)}
              active={filters.outcome === outcome}
              to={outcomeTo(outcome)}
            />
          )
        },
      },
    ],
  })

  const chartMatchesView = isPartnerView
    ? isPartnerSummary(summary)
    : !isPartnerSummary(summary)
  const isChartLoading = isInitialLoading || !chartMatchesView

  const tableContent = (() => {
    if (isInitialLoading) {
      return (
        <LoadingSpinner position="center" label="Loading completed jobs" />
      )
    }

    if (error) {
      return (
        <p className="text-sm text-destructive" role="alert">
          {error}
        </p>
      )
    }

    return (
      <>
        <ListTable
          columns={columns}
          data={jobs}
          getRowKey={(job) => job.id}
          emptyMessage="No completed jobs found."
          onRowClick={(job) => navigate(`/jobs/${job.id}`)}
          sort={{ columnId: sort.column, direction: sort.direction }}
          onSort={(columnId) =>
            updateSearch(filters, 1, chartType, nextJobsReportSort(sort, columnId))
          }
        />

        <PaginationLinks
          pagination={pagination}
          onPageChange={(nextPage) => updateSearch(filters, nextPage)}
          className="pt-4"
        />
      </>
    )
  })()

  const chartRows = isPartnerSummary(summary)
    ? summary.by_partner.map((row) => ({
        key: String(row.assigned_partner_id),
        label: row.assigned_partner_name,
        count: row.count,
      }))
    : summary.by_outcome.map((row) => ({
        key: row.outcome,
        label: formatJobOutcomeType(row.outcome),
        count: row.count,
      }))

  return (
    <>
      <JobsReportShell
        chartAriaLabel={
          isPartnerView ? 'SiteWorks partner breakdown' : 'Outcome breakdown'
        }
        chartTitle={chartTitle}
        isLoading={isChartLoading}
        error={error}
        onOpenFilters={() => setIsFiltersOpen(true)}
        chart={
          <ReportCategoryChart
            rows={chartRows}
            chartType={chartType}
            onChartTypeChange={(nextChartType) =>
              updateSearch(filters, page, nextChartType)
            }
            onCategoryClick={(key) =>
              navigate(
                isPartnerView
                  ? partnerTo(Number(key))
                  : outcomeTo(key as CompletedJobOutcome)
              )
            }
            emptyMessage="No completed jobs match the current filters."
          />
        }
        table={tableContent}
        filtersPanel={
          <JobsReportFiltersPanel
            open={isFiltersOpen}
            onOpenChange={setIsFiltersOpen}
            filters={filters}
            onApply={(nextFilters) => updateSearch(nextFilters, 1)}
            title="Filter completed jobs"
            idPrefix="completed-jobs-filter"
            emptyFilters={emptyCompletedJobsReportFilters}
            renderPrimaryDimension={(draft, setDraft) => (
              <SelectField
                id="completed-jobs-filter-outcome"
                label="Outcome"
                value={draft.outcome}
                onValueChange={(outcome) =>
                  setDraft((current) => ({
                    ...current,
                    outcome: outcome as CompletedJobOutcome | undefined,
                  }))
                }
                options={outcomeOptions}
                placeholder="All"
                allowEmpty
              />
            )}
          />
        }
      />
      <Outlet />
    </>
  )
}
